import React, { useEffect, useCallback, useMemo, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/i18n';
import {
  useClickOutsideHandler,
  useCallbackRef,
  ELEVATION_ATTR,
  getHighestVisibleElevation,
} from '@trello/layer-manager';
import { HeaderTestIds } from '@trello/test-ids';
import { AnalyticsAttributesProvider } from './AnalyticsAttributesProvider';
import styles from './BigNav.less';
import { Buttons } from './Buttons';
import { Logo } from './Logo';
import { Tabs } from './Tabs';
import { TabDetail } from './TabDetail';
import { TabId } from './TabId';
import { UiBanner } from './UiBanner';
import { UiNavLink } from './UiNavLink';

const format = forTemplate('logged_out_header');
const analyticsAttributes = { eventContainer: 'globalNav' };

interface BigNavProps {
  hasScrolled: boolean;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
}

export const BigNav: React.FunctionComponent<BigNavProps> = ({
  hasScrolled,
  onLoginClick,
  onSignupClick,
}) => {
  const [activeTab, setActiveTab] = useState<number | undefined>();

  function escPress(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      setActiveTab(undefined);
    }
  }

  useEffect(() => {
    if (activeTab !== undefined) {
      window.addEventListener('keydown', escPress);
      document.body.classList.add('prevent-scroll');
    }
    return () => {
      window.removeEventListener('keydown', escPress);
      document.body.classList.remove('prevent-scroll');
    };
  }, [activeTab]);

  useEffect(() => {
    const indexedLibrary = new Map();
    indexedLibrary.set(0, 'anonymousHeaderFeaturesInlineDialog');
    indexedLibrary.set(1, 'anonymousHeaderSolutionsInlineDialog');
    indexedLibrary.set(2, 'anonymousHeaderPlansInlineDialog');
    indexedLibrary.set(4, 'anonymousHeaderResourcesInlineDialog');
    if (indexedLibrary.get(activeTab)) {
      Analytics.sendScreenEvent({
        name: indexedLibrary.get(activeTab),
      });
    }
  }, [activeTab]);

  // close tab detail when clicks occur outside of it
  const [
    headerContentsElement,
    headerContentsRef,
  ] = useCallbackRef<HTMLDivElement>();
  const elevation = useMemo(() => getHighestVisibleElevation() + 1, []);
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      setActiveTab(undefined);
    },
    [setActiveTab],
  );
  useClickOutsideHandler({
    element: headerContentsElement,
    handleClickOutside,
  });

  return (
    <AnalyticsAttributesProvider value={analyticsAttributes}>
      <header
        className={styles.header}
        data-open={activeTab !== undefined}
        data-test-id={HeaderTestIds.LoggedOutHeaderWide}
      >
        <div
          className={styles.innerHeader}
          ref={headerContentsRef}
          {...{ [ELEVATION_ATTR]: elevation }}
          data-active={hasScrolled || activeTab !== undefined}
        >
          <div
            className={styles.navBar}
            data-test-id={HeaderTestIds.LoggedOutHeaderWideTabs}
          >
            <Logo navSize="big" />
            <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <Buttons
              onLoginClick={onLoginClick}
              onSignupClick={onSignupClick}
            />
          </div>
          <div className={styles.detailWrapper}>
            {activeTab === TabId.Features && (
              <TabDetail
                active={activeTab === TabId.Features}
                primaryLinks={[
                  <UiNavLink
                    key={`${TabId.Features}-0`}
                    titleHeading={format('features-primary-link-heading-views')}
                    description={format(
                      'features-primary-link-description-views',
                    )}
                    icon="New"
                    url="/views"
                    analyticsLinkName="viewsLink"
                    analyticsSource="anonymousHeaderFeaturesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Features}-1`}
                    titleHeading={format(
                      'features-primary-link-heading-automation',
                    )}
                    description={format(
                      'features-primary-link-description-automation',
                    )}
                    icon="ButlerAutomationHead"
                    url="/butler-automation"
                    analyticsLinkName="automationLink"
                    analyticsSource="anonymousHeaderFeaturesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Features}-2`}
                    titleHeading={format(
                      'features-primary-link-heading-power-ups',
                    )}
                    description={format(
                      'features-primary-link-description-power-ups',
                    )}
                    icon="RocketInclined"
                    url="/power-ups"
                    analyticsLinkName="powerupsLink"
                    analyticsSource="anonymousHeaderFeaturesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Features}-3`}
                    titleHeading={format(
                      'features-primary-link-heading-templates',
                    )}
                    description={format(
                      'features-primary-link-description-templates',
                    )}
                    icon="BrowserCode"
                    url="/templates"
                    analyticsLinkName="templatesLink"
                    analyticsSource="anonymousHeaderFeaturesInlineDialog"
                  />,
                ]}
                primaryTitle={format('features-tab-detail-heading')}
                secondaryButtonText={format(
                  'features-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="/tour"
                secondaryDescription={format(
                  'features-tab-detail-secondary-description',
                )}
                secondaryTitle={format('features-tab-detail-secondary-heading')}
                parentLabel={format('features-tab-label')}
                secondaryButtonAnalyticsLinkName="checkOutTrelloLink"
                analyticsSource="anonymousHeaderFeaturesInlineDialog"
              />
            )}
            {activeTab === TabId.Solutions && (
              <TabDetail
                active={activeTab === TabId.Solutions}
                primaryTitle={format('solutions-tab-detail-heading')}
                primaryLinks={[
                  <UiNavLink
                    key={`${TabId.Solutions}-0`}
                    titleHeading={format(
                      'solutions-primary-link-heading-marketing',
                    )}
                    description={format(
                      'solutions-primary-link-description-marketing',
                    )}
                    icon="Megaphone"
                    url="/teams/marketing"
                    analyticsLinkName="marketingTeamsLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-1`}
                    titleHeading={format(
                      'solutions-primary-link-heading-product',
                    )}
                    description={format(
                      'solutions-primary-link-description-product',
                    )}
                    icon="Box"
                    url="/teams/product"
                    analyticsLinkName="productManagementLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-2`}
                    titleHeading={format(
                      'solutions-primary-link-heading-engineering',
                    )}
                    description={format(
                      'solutions-primary-link-description-engineering',
                    )}
                    icon="BrowserCode"
                    url="/teams/engineering"
                    analyticsLinkName="engineeringTeamsLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-3`}
                    titleHeading={format(
                      'solutions-primary-link-heading-design',
                    )}
                    description={format(
                      'solutions-primary-link-description-design',
                    )}
                    icon="Pencil"
                    url="/teams/design"
                    analyticsLinkName="designTeamLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-4`}
                    titleHeading={format(
                      'solutions-primary-link-heading-startups',
                    )}
                    description={format(
                      'solutions-primary-link-description-startups',
                    )}
                    icon="Lego"
                    url="/teams/startups"
                    analyticsLinkName="startupsLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-5`}
                    titleHeading={format(
                      'solutions-primary-link-heading-remote',
                    )}
                    description={format(
                      'solutions-primary-link-description-remote',
                    )}
                    icon="GlobeNetworkLocation"
                    url="/teams/remote-team-management"
                    analyticsLinkName="remoteTeamsLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                ]}
                primaryLinkText={format(
                  'solutions-tab-detail-primary-link-text',
                )}
                primaryLinkUrl="/teams"
                secondaryTitle={format(
                  'solutions-tab-detail-secondary-heading',
                )}
                secondaryDescription={format(
                  'solutions-tab-detail-secondary-description',
                )}
                secondaryButtonText={format(
                  'solutions-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="/use-cases"
                secondaryLinks={[
                  <UiNavLink
                    key={`${TabId.Solutions}-6`}
                    addBackgroundColorOnHover={false}
                    titleHeading={format(
                      'solutions-secondary-link-heading-project-management',
                    )}
                    description={format(
                      'solutions-secondary-link-description-project-management',
                    )}
                    url="/use-cases/project-management"
                    decorator="Arrow Right"
                    analyticsLinkName="useCaseProjectManagementLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-7`}
                    addBackgroundColorOnHover={false}
                    titleHeading={format(
                      'solutions-secondary-link-heading-brainstorming',
                    )}
                    description={format(
                      'solutions-secondary-link-description-brainstorming',
                    )}
                    url="/use-cases/brainstorming"
                    decorator="Arrow Right"
                    analyticsLinkName="useCaseBrainstormingLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-8`}
                    addBackgroundColorOnHover={false}
                    titleHeading={format(
                      'solutions-secondary-link-heading-meetings',
                    )}
                    description={format(
                      'solutions-secondary-link-description-meetings',
                    )}
                    url="/use-cases/meetings"
                    decorator="Arrow Right"
                    analyticsLinkName="useCaseMeetingsLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Solutions}-9`}
                    addBackgroundColorOnHover={false}
                    titleHeading={format(
                      'solutions-secondary-link-heading-task-management',
                    )}
                    description={format(
                      'solutions-secondary-link-description-task-management',
                    )}
                    url="/use-cases/task-management"
                    decorator="Arrow Right"
                    analyticsLinkName="useCaseTaskManagementLink"
                    analyticsSource="anonymousHeaderSolutionsInlineDialog"
                  />,
                ]}
                primaryLinkAnalyticsName="seeAllTeamsLink"
                secondaryButtonAnalyticsLinkName="seeAllUseCasesLink"
                analyticsSource="anonymousHeaderSolutionsInlineDialog"
                parentLabel={format('solutions-tab-label')}
              />
            )}
            {activeTab === TabId.Plans && (
              <TabDetail
                active={activeTab === TabId.Plans}
                primaryBanner={
                  <UiBanner
                    type="uiBanner"
                    titleHeading={format('plans-primary-banner-header-free')}
                    description={format(
                      'plans-primary-banner-description-free',
                    )}
                    buttonText={format('plans-primary-banner-button-text-free')}
                    buttonUrl="/tour"
                    analyticsLinkName="takeATourOfTrelloLink"
                    analyticsSource="anonymousHeaderPlansInlineDialog"
                  />
                }
                primaryLinks={[
                  <UiNavLink
                    key={`${TabId.Plans}-0`}
                    titleHeading={format('plans-primary-link-header-standard')}
                    description={format(
                      'plans-primary-link-description-standard',
                    )}
                    icon="Lightbulb"
                    iconColor="Teal"
                    url="/standard"
                    analyticsLinkName="standardLink"
                    analyticsSource="anonymousHeaderPlansInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Plans}-1`}
                    titleHeading={format('plans-primary-link-header-premium')}
                    description={format(
                      'plans-primary-link-description-premium',
                    )}
                    icon="Star"
                    iconColor="Magenta"
                    url="/premium"
                    analyticsLinkName="premiumLink"
                    analyticsSource="anonymousHeaderPlansInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Plans}-2`}
                    titleHeading={format(
                      'plans-primary-link-header-enterprise',
                    )}
                    description={format(
                      'plans-primary-link-description-enterprise',
                    )}
                    icon="Building"
                    iconColor="Blue"
                    url="/enterprise"
                    analyticsLinkName="enterpriseLink"
                    analyticsSource="anonymousHeaderPlansInlineDialog"
                  />,
                ]}
                secondaryButtonText={format(
                  'plans-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="/pricing"
                secondaryDescription={format(
                  'plans-tab-detail-secondary-description',
                )}
                secondaryTitle={format(
                  'plans-tab-detail-secondary-heading',
                ).replace(/&amp;/g, '&')}
                secondaryButtonAnalyticsLinkName="viewTrelloPricingLink"
                analyticsSource="anonymousHeaderPlansInlineDialog"
                parentLabel={format('plans-tab-label')}
              />
            )}
            {activeTab === TabId.Resources && (
              <TabDetail
                active={activeTab === TabId.Resources}
                primaryLinks={[
                  <UiNavLink
                    key={`${TabId.Resources}-0`}
                    titleHeading={format(
                      'resources-primary-link-header-trello-guide',
                    )}
                    description={format(
                      'resources-primary-link-description-trello-guide',
                    )}
                    url="/guide"
                    analyticsLinkName="trelloGuideLink"
                    analyticsSource="anonymousHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Resources}-1`}
                    titleHeading={format(
                      'resources-primary-link-header-remote-guide',
                    )}
                    description={format(
                      'resources-primary-link-description-remote-guide',
                    )}
                    url="/guide/remote-work"
                    analyticsLinkName="remoteWorkGuideLink"
                    analyticsSource="anonymousHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Resources}-2`}
                    titleHeading={format('resources-primary-link-header-blog')}
                    description={format(
                      'resources-primary-link-description-blog',
                    )}
                    url="https://blog.trello.com"
                    analyticsLinkName="trelloBlogLink"
                    analyticsSource="anonymousHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Resources}-3`}
                    titleHeading={format(
                      'resources-primary-link-header-webinars',
                    )}
                    description={format(
                      'resources-primary-link-description-webinars',
                    )}
                    url="/webinars"
                    analyticsLinkName="webinarsLink"
                    analyticsSource="anonymousHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Resources}-4`}
                    titleHeading={format(
                      'resources-primary-link-header-developers',
                    )}
                    description={format(
                      'resources-primary-link-description-developers',
                    )}
                    url="https://developer.atlassian.com/cloud/trello"
                    analyticsLinkName="developersLink"
                    analyticsSource="anonymousHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    key={`${TabId.Resources}-5`}
                    titleHeading={format('resources-primary-link-header-help')}
                    description={format(
                      'resources-primary-link-description-help',
                    )}
                    url="https://help.trello.com"
                    analyticsLinkName="helpResourcesLink"
                    analyticsSource="anonymousHeaderResourcesInlineDialog"
                  />,
                ]}
                primaryTitle={format('resources-tab-detail-heading').replace(
                  /&amp;/g,
                  '&',
                )}
                secondaryButtonText={format(
                  'resources-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="https://blog.trello.com"
                secondaryDescription={format(
                  'resources-tab-detail-secondary-description',
                )}
                secondaryTitle={format(
                  'resources-tab-detail-secondary-heading',
                )}
                secondaryButtonAnalyticsLinkName="checkOutTheTrelloBlogLink"
                analyticsSource="anonymousHeaderResourcesInlineDialog"
                parentLabel={format('resources-tab-label')}
              />
            )}
          </div>
        </div>
      </header>
    </AnalyticsAttributesProvider>
  );
};
